/**
 * Convert a CrosswordGrid into a list of YearbookElement entries placed on a page.
 *
 * Layout:
 *  - Native rect shape per cell + small text per number. All share the same
 *    groupId so they are selected/moved/deleted as a single object.
 *  - A separate text block next to the grid lists Horizontal / Vertical clues.
 */

import { CrosswordGrid } from './crosswordGenerator';
import { YearbookElement, DEFAULT_TEXT_STYLE, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';

const SAFE_MARGIN = 14.17; // 5mm
const MIN_CELL = 16;
const MAX_CELL = 36;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export interface BuildOptions {
  font?: string;
}

export function buildCrosswordElements(
  grid: CrosswordGrid,
  _title: string,
  options: BuildOptions = {}
): YearbookElement[] {
  const { font = DEFAULT_TEXT_STYLE.fontFamily } = options;
  if (grid.rows === 0 || grid.cols === 0) return [];

  const usableW = PAGE_WIDTH - 2 * SAFE_MARGIN;
  const ANSWERS_H = 46; // bottom strip reserved for the upside-down answers
  const usableH = PAGE_HEIGHT - 2 * SAFE_MARGIN - ANSWERS_H;

  const clueRatio = 0.42;
  const gridAreaW = usableW * (1 - clueRatio) - 10;
  const gridAreaH = usableH;

  const cell = Math.max(
    MIN_CELL,
    Math.min(MAX_CELL, Math.floor(Math.min(gridAreaW / grid.cols, gridAreaH / grid.rows)))
  );

  const gridW = cell * grid.cols;
  const gridH = cell * grid.rows;

  const gridX = SAFE_MARGIN + (gridAreaW - gridW) / 2;
  const gridY = SAFE_MARGIN + (gridAreaH - gridH) / 2;

  const groupId = `cw-${generateId()}`;
  const elements: YearbookElement[] = [];

  for (const c of grid.cells) {
    const x = gridX + c.col * cell;
    const y = gridY + c.row * cell;
    elements.push({
      id: generateId(),
      type: 'shape',
      shapeType: 'rect',
      position: { x, y },
      size: { width: cell, height: cell },
      rotation: 0,
      backgroundColor: '#FFFFFF',
      borderColor: '#000000',
      strokeWidth: 1,
      groupId,
      textStyle: {
        fontSize: 0,
        fontFamily: '',
        fontWeight: '',
        color: '#FFFFFF',
        textAlign: 'left',
        italic: false,
        underline: false,
      },
    });

    if (c.number) {
      const numSize = Math.max(6, Math.floor(cell * 0.32));
      elements.push({
        id: generateId(),
        type: 'text',
        position: { x: x + 1.5, y: y + 0.5 },
        size: { width: cell * 0.6, height: numSize + 2 },
        rotation: 0,
        content: String(c.number),
        groupId,
        textStyle: {
          ...DEFAULT_TEXT_STYLE,
          fontFamily: font,
          fontSize: numSize,
          fontWeight: '600',
          textAlign: 'left',
          color: '#1a1a1a',
        },
      });
    }
  }

  // Clue text block on the right (NOT part of the group — independent).
  const clueX = SAFE_MARGIN + gridAreaW + 10;
  const clueY = SAFE_MARGIN;
  const clueW = usableW - gridAreaW - 10;
  const clueH = usableH;

  const acrossLines = grid.across.map((a) => `${a.number}. ${a.clue || a.display}`);
  const downLines = grid.down.map((d) => `${d.number}. ${d.clue || d.display}`);

  const content =
    (acrossLines.length ? `HORIZONTAL\n${acrossLines.join('\n')}\n\n` : '') +
    (downLines.length ? `VERTICAL\n${downLines.join('\n')}` : '');

  if (content.trim()) {
    elements.push({
      id: generateId(),
      type: 'text',
      position: { x: clueX, y: clueY },
      size: { width: clueW, height: clueH },
      rotation: 0,
      content,
      textStyle: {
        ...DEFAULT_TEXT_STYLE,
        fontFamily: font,
        fontSize: 9,
        fontWeight: '400',
        textAlign: 'left',
        color: '#1a1a1a',
      },
    });
  }

  // Answers block at the bottom, printed upside down (rotated 180°).
  const answers = [...grid.across, ...grid.down]
    .sort((a, b) => a.number - b.number)
    .map((c) => `${c.number} : ${c.display}`);

  if (answers.length) {
    const answersH = 40;
    elements.push({
      id: generateId(),
      type: 'text',
      position: { x: SAFE_MARGIN, y: PAGE_HEIGHT - SAFE_MARGIN - answersH },
      size: { width: usableW, height: answersH },
      rotation: 180,
      content: `SOLUTIONS : ${answers.join(' - ')}`,
      textStyle: {
        ...DEFAULT_TEXT_STYLE,
        fontFamily: font,
        fontSize: 8,
        fontWeight: '400',
        textAlign: 'center',
        color: '#1a1a1a',
      },
    });
  }

  return elements.map((e) => ({ ...e, userAdded: true }));
}
